import { Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { CircularProgress, IconButton } from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getAllShares } from "../../services/shareService";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  GetSharesResponse,
  Share,
  ShareType,
  ShareWithAnalyticsEvent,
  SubscriptionTier,
} from "../../proto/api_pb";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import { styles, TableScrollContainer, TableTitleContainer } from "./styles";
import { ViewsContainer } from "./styles";
import { MaxViewsContainer, ShareContainer } from "./styles";
import { DescriptionContainer, ExpiryDateContainer } from "./styles";
import { ActionsContainer } from "./styles";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../Loading";
import { ShareDetailsModal } from "../../components/Modal/ShareDetailsModal";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { ShareAnalyticsModal } from "../../components/Modal/ShareAnalyticsModal";

const PAGE_SIZE = 20;

const ShareManagement: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [shares, setShares] = useState<GetSharesResponse>();
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const [isShareUpdateModalOpen, setIsShareUpdateModalOpen] =
    useState<boolean>(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] =
    useState<boolean>(false);
  const [selectedShareForAnalytics, setSelectedShareForAnalytics] =
    useState<ShareWithAnalyticsEvent>();
  const [selectedShareForUpdate, setSelectedShareForUpdate] = useState<Share>();
  const { getValidAccessToken } = useGetValidAccessToken();
const { isEnterpriseAccount, enterprise, subscriptionTier } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const getTimestampToDate = (timeStamp: number, isExpiryTime?: boolean) => {
    const date = isExpiryTime
      ? new Date(timeStamp)
      : new Date(timeStamp * 1000);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const amPm = hours >= 12 ? "PM" : "AM";
    const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${month}/${day}/${year} ${formattedHours}:${formattedMinutes} ${amPm}`;
  };

  const getSharesList = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getValidAccessToken();
      const getSharesResponse = await getAllShares(
        userId,
        token,
        isEnterpriseAccount ? enterprise?.id : undefined
      );
      if (getSharesResponse instanceof GetSharesResponse) {
        setShares(getSharesResponse);
        setLoading(false);
      }
    } catch (er) {
      console.log(er);
    }
  }, [enterprise?.id, getValidAccessToken, isEnterpriseAccount, userId]);

  useEffect(() => {
    getSharesList();
  }, [getValidAccessToken, getSharesList, userId]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [shares]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      setVisibleCount((prev) => prev + PAGE_SIZE);
    }
  }, []);

  const handleCopyShareUrl = (shareLink: string) => {
    if (shareLink) {
      navigator.clipboard
        .writeText(shareLink)
        .then(() => {
          console.log("URL successfully copied to clipboard");
          toast.info("URL copied");
        })
        .catch((err) => {
          console.error("Unable to copy URL to clipboard", err);
        });
    }
  };

  const getShareTypeLabel = (share: ShareWithAnalyticsEvent) => {
    switch (share.getShare()?.getShareType()) {
      case ShareType.SHARE_TYPE_TRACK:
        return "Track";
      case ShareType.SHARE_TYPE_TRACK_FILE:
        return "Track File";
      case ShareType.SHARE_TYPE_FOLDER:
        return "Folder";
      case ShareType.SHARE_TYPE_PROJECT:
        return "Project";
      case ShareType.SHARE_TYPE_PROJECTS_COLLECTION:
        return "Projects";
      default:
        return "Unknown";
    }
  };

  return (
    <>
      <MobileDrawer />
      <Box sx={{ display: "flex" }}>
        <Box sx={{ width: "100%", padding: "20px" }}>
          <Typography variant="h4">Shared Tracks</Typography>
          <TableTitleContainer>
            <Box sx={{ width: "16%" }}>Title</Box>
            <Box sx={{ minWidth: "50px" }}>Share Type</Box>
            <Box sx={{ minWidth: "50px" }}>
              Total Views
              <Tooltip
                placement="top"
                title="View is counted after 3 seconds of track playback or when a project/folder page is visited."
              >
                <span style={styles.infoCircle}>i</span>
              </Tooltip>
            </Box>
            <Box sx={{ minWidth: "50px", marginLeft: -2 }}>Max Views</Box>
            <Box sx={{ width: "180px" }}>Expiry Time</Box>
            <Box sx={{ width: "130px" }}>Actions</Box>
          </TableTitleContainer>
          {loading ? (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : !isEnterpriseAccount &&
            (subscriptionTier === SubscriptionTier.SUBSCRIPTION_TIER_FREE ||
              subscriptionTier === SubscriptionTier.SUBSCRIPTION_TIER_MINOR) ? (
            <Box sx={styles.noShareFoundBox}>
              <Typography>
                Please upgrade your subscription to see details!
              </Typography>
            </Box>
          ) : shares?.getSharesList().length === 0 ||
            shares?.getSharesList() === undefined ? (
            <Box sx={styles.noShareFoundBox}>
              <Typography>No shares found!</Typography>
            </Box>
          ) : (
            <TableScrollContainer
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                height: isSmallScreen
                  ? "calc(100vh - 225px)"
                  : "calc(100vh - 156px)",
              }}
            >
              {shares?.getSharesList().slice(0, visibleCount).map((share, i) => {
                return (
                  <ShareContainer
                    key={i}
                    sx={
                      i === shares.getSharesList().length - 1
                        ? {
                            borderBottomLeftRadius: "10px",
                            borderBottomRightRadius: "10px",
                          }
                        : {}
                    }
                  >
                    <DescriptionContainer
                      title={`${share.getShare()?.getTrackTitle()}`}
                    >
                      {share.getShare()?.getTrackTitle() === ""
                        ? "------"
                        : share.getShare()?.getTrackTitle()}
                    </DescriptionContainer>
                    <ViewsContainer>{getShareTypeLabel(share)}</ViewsContainer>
                    <ViewsContainer>
                      {share.getShare()?.getImpressionCount()}
                    </ViewsContainer>
                    <MaxViewsContainer>
                      {share.getShare()?.getUnlimitedViews()
                        ? "Unlimited"
                        : share.getShare()?.getMaxViews()}
                    </MaxViewsContainer>
                    <ExpiryDateContainer>
                      {share.getShare()?.getExpiryTimestamp() !== undefined
                        ? getTimestampToDate(
                            share.getShare()!.getExpiryTimestamp(),
                            true
                          )
                        : "No Expiry Date"}
                    </ExpiryDateContainer>
                    <ActionsContainer>
                      <Tooltip title="Analytics" placement="top">
                        <IconButton
                          onClick={() => {
                            setIsAnalyticsModalOpen(true);
                            setSelectedShareForAnalytics(share);
                          }}
                        >
                          <AnalyticsIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        placement="top"
                        title={
                          share.getShare() &&
                          !share.getShare()!.getUnlimitedViews() &&
                          share.getShare()!.getExpiryTimestamp() < Date.now()
                            ? "Expired"
                            : "Preview"
                        }
                      >
                        <span>
                          <IconButton
                            disabled={
                              share.getShare()
                                ? !share.getShare()!.getUnlimitedViews() &&
                                  share.getShare()!.getExpiryTimestamp() < Date.now()
                                : false
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              const sType = share.getShare()?.getShareType();
                              const sId = share.getShare()?.getId();
                              const sTitle = share.getShare()?.getTrackTitle();
                              let previewUrl = "";
                              if (sType === ShareType.SHARE_TYPE_PROJECT) {
                                previewUrl = `/share/project/${sTitle}/${sId}`;
                              } else if (sType === ShareType.SHARE_TYPE_FOLDER) {
                                previewUrl = `/share/${sTitle}/${sId}`;
                              } else if (sType === ShareType.SHARE_TYPE_PROJECTS_COLLECTION) {
                                previewUrl = `/share/projects/${sId}`;
                              } else {
                                previewUrl = `/share/${sId}`;
                              }
                              window.location.href = previewUrl;
                            }}
                          >
                            <PreviewIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        placement="top"
                        title={
                          share.getShare() &&
                          !share.getShare()!.getUnlimitedViews() &&
                          (share.getShare()?.getExpiryTimestamp() ?? Infinity) < Date.now()
                            ? "Expired"
                            : "Copy"
                        }
                      >
                        <span>
                          <IconButton
                            disabled={
                              share.getShare()
                                ? !share.getShare()!.getUnlimitedViews() &&
                                  share.getShare()!.getExpiryTimestamp() < Date.now()
                                : false
                            }
                            onClick={() => {
                              const cType = share.getShare()?.getShareType();
                              const cId = share.getShare()?.getId();
                              const cTitle = share.getShare()?.getTrackTitle();
                              if (cType === ShareType.SHARE_TYPE_PROJECT) {
                                handleCopyShareUrl(`${window.location.origin}/share/project/${cTitle}/${cId}`);
                              } else if (cType === ShareType.SHARE_TYPE_FOLDER) {
                                handleCopyShareUrl(`${window.location.origin}/share/${cTitle}/${cId}`);
                              } else if (cType === ShareType.SHARE_TYPE_PROJECTS_COLLECTION) {
                                handleCopyShareUrl(`${window.location.origin}/share/projects/${cId}`);
                              } else {
                                handleCopyShareUrl(`${window.location.origin}/share/${cId}`);
                              }
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit" placement="top">
                        <IconButton
                          onClick={() => {
                            if (
                              setSelectedShareForUpdate &&
                              setIsShareUpdateModalOpen
                            ) {
                              setSelectedShareForUpdate(share.getShare());
                              setIsShareUpdateModalOpen(true);
                            }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </ActionsContainer>
                  </ShareContainer>
                );
              })}
              {visibleCount < (shares?.getSharesList().length ?? 0) && (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </TableScrollContainer>
          )}
        </Box>
      </Box>
      {isShareUpdateModalOpen && (
        <ShareDetailsModal
          isShareDetailsModalOpen={isShareUpdateModalOpen}
          setIsShareDetailsModalOpen={setIsShareUpdateModalOpen}
          id={selectedShareForUpdate?.getId()}
          title={selectedShareForUpdate?.getTrackTitle()}
          shareType={selectedShareForUpdate?.getShareType() ?? 0}
          shareForUpdate={selectedShareForUpdate}
          isUpdateModal={true}
          getSharesList={getSharesList}
        />
      )}
      {isAnalyticsModalOpen && (
        <ShareAnalyticsModal
          open={isAnalyticsModalOpen}
          setOpen={setIsAnalyticsModalOpen}
          selectedShareForAnalytics={selectedShareForAnalytics}
        />
      )}
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
    </>
  );
};

export default withAuthenticationRequired(ShareManagement, {
  onRedirecting: () => <Loading />,
});
